import { useState } from "react";
import { useAppendContext, useGameplanContext } from "../hooks/useGameplans";

export function CampaignContextFeed({
  workspaceId,
  campaignId,
  canAppend,
}: {
  workspaceId: string;
  campaignId: string;
  canAppend: boolean;
}) {
  const q = useGameplanContext(workspaceId, campaignId);
  const append = useAppendContext(workspaceId, campaignId);
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [confidence, setConfidence] = useState<
    "confirmed" | "suspected" | "unconfirmed"
  >("confirmed");

  function submit() {
    if (!key.trim()) return;
    // Try JSON, fall back to string so users can paste either.
    let parsed: unknown = value;
    try {
      parsed = JSON.parse(value);
    } catch {
      parsed = value;
    }
    append.mutate(
      { key: key.trim(), value: parsed, confidence },
      {
        onSuccess: () => {
          setKey("");
          setValue("");
          setConfidence("confirmed");
          setOpen(false);
        },
      },
    );
  }

  return (
    <div data-testid="campaign-context-feed">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: "var(--text-3)",
            letterSpacing: 0.4,
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          Shared context
        </div>
        {canAppend ? (
          <button
            type="button"
            className="btn"
            style={{ fontSize: 11, padding: "4px 8px" }}
            onClick={() => setOpen((o) => !o)}
            data-testid="campaign-context-toggle"
          >
            {open ? "Cancel" : "+ Add entry"}
          </button>
        ) : null}
      </div>

      {open && canAppend ? (
        <div
          style={{
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: 10,
            marginBottom: 10,
            background: "var(--bg-soft)",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <input
            placeholder="key (e.g. affected_host)"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            data-testid="campaign-context-key"
            style={{
              padding: 6,
              fontSize: 12,
              border: "1px solid var(--border)",
              borderRadius: 4,
            }}
          />
          <textarea
            placeholder="value (string or JSON)"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={2}
            data-testid="campaign-context-value"
            style={{
              padding: 6,
              fontSize: 12,
              border: "1px solid var(--border)",
              borderRadius: 4,
              fontFamily: "inherit",
            }}
          />
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <select
              value={confidence}
              onChange={(e) =>
                setConfidence(e.target.value as typeof confidence)
              }
              data-testid="campaign-context-confidence"
              style={{ fontSize: 12, padding: "4px 6px" }}
            >
              <option value="confirmed">confirmed</option>
              <option value="suspected">suspected</option>
              <option value="unconfirmed">unconfirmed</option>
            </select>
            <button
              type="button"
              className="btn primary"
              onClick={submit}
              disabled={!key.trim() || append.isPending}
              data-testid="campaign-context-submit"
              style={{ fontSize: 12, padding: "4px 10px" }}
            >
              {append.isPending ? "Saving…" : "Add"}
            </button>
          </div>
          {append.error ? (
            <div style={{ color: "var(--danger)", fontSize: 11 }}>
              {(append.error as Error).message}
            </div>
          ) : null}
        </div>
      ) : null}

      {q.isLoading ? (
        <div style={{ fontSize: 12, color: "var(--text-3)" }}>Loading…</div>
      ) : q.error ? (
        <div style={{ fontSize: 12, color: "var(--danger)" }}>
          Failed to load context.
        </div>
      ) : (q.data?.entries.length ?? 0) === 0 ? (
        <div
          style={{
            fontSize: 12,
            color: "var(--text-3)",
            padding: 16,
            textAlign: "center",
            border: "1px dashed var(--border)",
            borderRadius: 8,
          }}
        >
          No context entries yet.
        </div>
      ) : (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {q.data!.entries.map((e) => (
            <li
              key={e.id}
              style={{
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: 8,
                fontSize: 12,
                background: "var(--bg)",
              }}
              data-testid={`campaign-context-entry-${e.id}`}
            >
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  alignItems: "center",
                  marginBottom: 4,
                }}
              >
                <span
                  style={{
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: 11,
                    background: "var(--bg-soft)",
                    padding: "1px 6px",
                    borderRadius: 3,
                  }}
                >
                  {e.key}
                </span>
                <ConfidencePill confidence={e.confidence} />
                <span
                  style={{
                    marginLeft: "auto",
                    color: "var(--text-3)",
                    fontSize: 10,
                  }}
                >
                  {new Date(e.createdAt).toLocaleString()}
                </span>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--text)",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {formatValue(e.value)}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "var(--text-3)",
                  marginTop: 4,
                }}
              >
                source: {e.source}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ConfidencePill({
  confidence,
}: {
  confidence: "confirmed" | "suspected" | "unconfirmed";
}) {
  const map = {
    confirmed: { bg: "#dcfce7", fg: "#166534" },
    suspected: { bg: "#fef3c7", fg: "#854d0e" },
    unconfirmed: { bg: "#f1f5f9", fg: "#475569" },
  }[confidence];
  return (
    <span
      style={{
        fontSize: 10,
        padding: "1px 6px",
        borderRadius: 3,
        background: map.bg,
        color: map.fg,
        fontWeight: 600,
        letterSpacing: 0.2,
      }}
    >
      {confidence}
    </span>
  );
}

function formatValue(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}
